/**
 * Food expiry API client (backend/routes/expiry.js). Same shape as the other
 * module clients: axios + getApiBaseUrl + credentials, data unwrapped.
 */

import axios from 'axios';
import { getApiBaseUrl } from '../../utils/apiClient';

const BASE = () => `${getApiBaseUrl()}/api/expiry`;
const opts = { withCredentials: true };

/** The server caps a bulk toggle at 500 items; bigger selections go in chunks. */
export const EXPIRY_BULK_LIMIT = 500;

export const EXPIRY_STORES = [
  { value: 'phaser-fpv.myshopify.com', label: 'Phaser FPV' },
  { value: 'central-coast-coffee.myshopify.com', label: 'Central Coast Coffee' }
];

/** Display name for a myshopify domain; the API's storeLabel wins when present. */
export function expiryStoreLabel(store, storeLabel) {
  if (storeLabel) return storeLabel;
  const known = EXPIRY_STORES.find((s) => s.value === store);
  if (known) return known.label;
  return String(store || '').replace(/\.myshopify\.com$/, '') || 'Unknown store';
}

export const expiryApi = {
  /** { storeKey: 'phaser'|'ccc' } or { store: domain }, plus skus -> ['SKU', ...] upper-cased. */
  lookupTracked: ({ storeKey, store, skus }) =>
    // Timeout so a hung lookup never leaves the receipt modal waiting forever.
    axios.post(`${BASE()}/tracked/lookup`, { ...(storeKey ? { storeKey } : { store }), skus }, { ...opts, timeout: 15000 })
      .then((r) => (Array.isArray(r.data?.tracked) ? r.data.tracked : [])),

  setTracked: (store, sku, tracked) =>
    axios.put(`${BASE()}/tracked`, { store, sku, tracked: Boolean(tracked) }, opts).then((r) => r.data),

  /** items: [{ store, sku }]. Chunked to the server's 500 cap; returns the summed count. */
  bulkSetTracked: async (items, tracked) => {
    const list = Array.isArray(items) ? items : [];
    let count = 0;
    for (let i = 0; i < list.length; i += EXPIRY_BULK_LIMIT) {
      const chunk = list.slice(i, i + EXPIRY_BULK_LIMIT);
      // eslint-disable-next-line no-await-in-loop
      const { data } = await axios.post(`${BASE()}/tracked/bulk`, { items: chunk, tracked: Boolean(tracked) }, opts);
      count += Number(data?.count) || 0;
    }
    return { ok: true, count };
  },

  getBatches: (store, sku) =>
    axios.get(`${BASE()}/batches`, { ...opts, params: { store, sku } }).then((r) => r.data),

  addBatch: ({ store, sku, qty, expiryDate }) =>
    axios.post(`${BASE()}/batches`, { store, sku, qty, expiryDate }, opts).then((r) => r.data),

  writeOff: (id, reason) =>
    axios.post(`${BASE()}/batches/${encodeURIComponent(id)}/write-off`, reason ? { reason } : {}, opts)
      .then((r) => r.data),

  restore: (id) =>
    axios.post(`${BASE()}/batches/${encodeURIComponent(id)}/restore`, {}, opts).then((r) => r.data),

  /** store: 'all' or a myshopify domain. */
  getDashboard: (store = 'all') =>
    axios.get(`${BASE()}/dashboard`, { ...opts, params: { store } }).then((r) => r.data)
};

/** Friendly message from an axios error. */
export function readExpiryError(error, fallback = 'Something went wrong.') {
  const data = error?.response?.data;
  return data?.error || data?.message || error?.message || fallback;
}
